import { RadioGroup } from "devextreme-react/radio-group";
import { TextArea } from "devextreme-react/text-area";
import { TextBox } from "devextreme-react/text-box";
import { ValidationSummary } from "devextreme-react/validation-summary";
import { Box, Item } from "devextreme-react/box";
import Button from "devextreme-react/button";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import { useContext, useRef, useState } from "react";
import { useGetLookupValuesByType } from "../../../../api/lookup";
import { Field } from "../../../../layouts";
import { OrderStatus } from "../../../../types/enums";
import { useRequestCancellation } from "../api/request-cancellation";
import { RetailOrderContext } from "../context/RetailOrderContext";
import Validator, { RequiredRule } from "devextreme-react/validator";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { LoadPopupPanel } from "../../../../components";

export default function RequestCancellationButton() {
	const REFUND_REBILL_REASON = 900;
	const OTHER_REASON = 910;

	const { order, refetchOrder } = useContext(RetailOrderContext);

	const [isPopupVisible, setPopupVisibility] = useState(false);
	const [selectedReason, setSelectedReason] = useState(order.details.cancellationReason ?? REFUND_REBILL_REASON);

	const cancellationReasons = useGetLookupValuesByType('CFIReasons', isPopupVisible);

	const commentRef = useRef<TextArea>(null);
	const dealerNewPONumberRef = useRef<TextBox>(null);

	const requestCancellation = useRequestCancellation(
		() => {
			setPopupVisibility(false);
			showSuccessMessage('Cancellation request has been sent');
			refetchOrder();
		},
		(error) => showErrorMessage(error)
	)

	return (
		<>
			<Button
				elementAttr={{ 'data-testid': 'btn-request-ro-cancellation' }}
				text="RO Cancellation"
				icon="Inscyth-Cancel"
				visible={order.details.orderStatusID !== OrderStatus.Cancelled &&
					order.details.orderStatusID !== OrderStatus.Closed &&
					!order.details.cancellationReason}
				disabled={isPopupVisible}
				onClick={() => setPopupVisibility(true)}
			>
			</Button>
			<Popup
				title="Request Retail Order Cancellation"
				width={700}
				height={550}
				visible={isPopupVisible}
				hideOnOutsideClick={true}
				onHiding={() => setPopupVisibility(false)}
			>
				<ToolbarItem
					options={{
						icon: 'dx-icon dx-icon-close',
						onClick: function () {
							setPopupVisibility(false);
						}
					}}
					widget="dxButton"
					location="after"
				/>
				<ValidationSummary validationGroup="requestCancellationGroup"></ValidationSummary>
				<Box>
					<Item ratio={0} baseSize={250}>
						<RadioGroup
							dataSource={cancellationReasons.data}
							valueExpr={'lookupID'}
							displayExpr={'displayValue'}
							value={selectedReason}
							onValueChanged={(e) => {
								setSelectedReason(e.value);
								if (e.value !== REFUND_REBILL_REASON)
									dealerNewPONumberRef.current?.instance.option('value', '');
								if (e.value !== OTHER_REASON)
									commentRef.current?.instance.option('value', '');
							}}
						/>
					</Item>
					<Item ratio={1}>
						<Field label={'Dealer New PO #'} vertical={true} className="black-label"
							visible={selectedReason === REFUND_REBILL_REASON}>
							<TextBox ref={dealerNewPONumberRef}>
								<Validator validationGroup="requestCancellationGroup">
									<RequiredRule message={'Dealer New PO # is required'} />
								</Validator>
							</TextBox>
						</Field>
						<Field label={'Comment'}
							className="black-label"
							vertical={true}
							visible={selectedReason === OTHER_REASON}>
							<TextArea ref={commentRef} height={250}>
								<Validator validationGroup="requestCancellationGroup">
									<RequiredRule message={'Comment is required'} />
								</Validator>
							</TextArea>
						</Field>
					</Item>
				</Box>
				<ToolbarItem
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Submit',
						validationGroup: 'requestCancellationGroup',
						onClick: (e: any) => {
							if (!e.validationGroup || e.validationGroup.validate().isValid) {
								requestCancellation.mutate({
									retailOrderID: order.details.retailOrderID,
									cancellationReason: selectedReason,
									newPONumber: dealerNewPONumberRef.current?.instance.option("value"),
									comment: commentRef.current?.instance.option("value")
								});
							}
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				>
				</ToolbarItem>
				<ToolbarItem
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Cancel',
						onClick: () => setPopupVisibility(false)
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				>
				</ToolbarItem>
				<LoadPopupPanel visible={cancellationReasons.isLoading || requestCancellation.isLoading}></LoadPopupPanel>
			</Popup>
		</>
	)
}
